import { apiRequest } from "./apiClient";
import { rt } from "../i18n/runtime";

export const DEFAULT_NOTIFICATION_POLICY_GLOBAL = {
  key: "global",
  title: rt("policy.global.title"),
  enabled: true,
  globalQuietHoursEnabled: true,
  quietStart: "22:00",
  quietEnd: "08:00",
  timezoneMode: "recipient_local",
  cronIntervalMinutes: 1,
};

export const DEFAULT_NOTIFICATION_POLICIES = [
  {
    key: "shoppingReminder",
    icon: "🛒",
    title: rt("policy.shopping.title"),
    description: rt("policy.shopping.desc"),
    enabled: true,
    dailyLimit: 1,
    respectGlobalQuietHours: true,
    preventDuplicates: true,
    rules: [
      { key: "pendingItems", label: rt("policy.shopping.pending"), enabled: true, time: "18:00" },
    ],
  },
  {
    key: "taskReminder",
    icon: "📋",
    title: rt("policy.task.title"),
    description: rt("policy.task.desc"),
    enabled: true,
    dailyLimit: 3,
    respectGlobalQuietHours: true,
    preventDuplicates: true,
    rules: [
      { key: "threeDaysBefore", label: rt("policy.rule.3days"), enabled: true, daysBefore: 3, time: "18:00" },
      { key: "tomorrow", label: rt("policy.rule.1day"), enabled: true, daysBefore: 1, time: "20:00" },
      { key: "today", label: rt("policy.rule.today"), enabled: true, daysBefore: 0, time: "09:00" },
      { key: "overdue", label: rt("policy.task.overdue"), enabled: false, type: "overdue", time: "10:00" },
    ],
  },
  {
    key: "calendarReminder",
    icon: "📅",
    title: rt("policy.calendar.title"),
    description: rt("policy.calendar.desc"),
    enabled: true,
    dailyLimit: 5,
    respectGlobalQuietHours: true,
    preventDuplicates: true,
    rules: [
      { key: "tomorrow", label: rt("notification.oneDayBefore"), enabled: true, daysBefore: 1, time: "20:00" },
      { key: "today", label: rt("policy.calendar.today"), enabled: true, daysBefore: 0, time: "08:30" },
      { key: "thisWeek", label: rt("policy.calendar.week"), enabled: true, type: "weekly", time: "09:00" },
    ],
  },
  {
    key: "documentExpiry",
    icon: "📁",
    title: rt("policy.document.title"),
    description: rt("policy.document.desc"),
    enabled: true,
    dailyLimit: 3,
    respectGlobalQuietHours: true,
    preventDuplicates: true,
    rules: [
      { key: "thirtyDaysBefore", label: rt("policy.rule.30days"), enabled: true, daysBefore: 30, time: "09:00" },
      { key: "tenDaysBefore", label: rt("policy.rule.10days"), enabled: true, daysBefore: 10, time: "09:00" },
      { key: "fiveDaysBefore", label: rt("policy.rule.5days"), enabled: true, daysBefore: 5, time: "09:00" },
      { key: "oneDayBefore", label: rt("policy.rule.1day"), enabled: true, daysBefore: 1, time: "09:00" },
      { key: "today", label: rt("policy.document.today"), enabled: true, daysBefore: 0, time: "08:00" },
      { key: "expired", label: rt("policy.document.expired"), enabled: true, type: "expired", time: "10:00" },
    ],
  },
  {
    key: "healthReminder",
    icon: "❤️",
    title: rt("policy.health.title"),
    description: rt("policy.health.desc"),
    enabled: true,
    dailyLimit: 12,
    respectGlobalQuietHours: true,
    preventDuplicates: true,
    rules: [
      { key: "appointmentsTomorrow", label: rt("policy.health.appt1day"), enabled: true, daysBefore: 1, time: "20:00" },
      { key: "appointmentsToday", label: rt("policy.health.apptToday"), enabled: true, daysBefore: 0, time: "08:00" },
      { key: "medicines", label: rt("policy.health.meds"), enabled: true, usesOwnTime: true, snoozeMinutes: 10, maxRepeat: 3 },
    ],
  },
  {
    key: "financeReminder",
    icon: "💰",
    title: rt("policy.finance.title"),
    description: rt("policy.finance.desc"),
    enabled: true,
    dailyLimit: 8,
    deliverySpreadMinutes: 120,
    respectGlobalQuietHours: true,
    preventDuplicates: true,
    rules: [
      { key: "installmentBefore", label: rt("policy.finance.installmentBefore"), enabled: true, financeKind: "installment", daysBefore: 1, time: "16:00" },
      { key: "installmentToday", label: rt("policy.finance.installmentToday"), enabled: true, financeKind: "installment", daysBefore: 0, time: "09:00" },
      { key: "subscriptionBefore", label: rt("policy.finance.subscriptionBefore"), enabled: true, financeKind: "subscription", daysBefore: 3, time: "16:00" },
      { key: "subscriptionToday", label: rt("policy.finance.subscriptionToday"), enabled: true, financeKind: "subscription", daysBefore: 0, time: "09:30" },
      { key: "billBefore", label: rt("policy.finance.billBefore"), enabled: true, financeKind: "bill", daysBefore: 3, time: "17:00" },
      { key: "billToday", label: rt("policy.finance.billToday"), enabled: true, financeKind: "bill", daysBefore: 0, time: "09:00" },
      { key: "savingValueStale", label: rt("policy.finance.savingValue"), enabled: true, type: "savingValue", thresholdDays: 30, repeatEveryDays: 14, maxRepeat: 3, time: "11:00" },
      { key: "savingContributionStale", label: rt("policy.finance.savingContribution"), enabled: true, type: "savingContribution", thresholdDays: 45, repeatEveryDays: 21, maxRepeat: 3, time: "11:30" },
    ],
  },
];

const defaultsByKey = () => {
  const acc = { global: DEFAULT_NOTIFICATION_POLICY_GLOBAL };
  DEFAULT_NOTIFICATION_POLICIES.forEach((policy) => {
    acc[policy.key] = policy;
  });
  return acc;
};

export const mergeNotificationPolicy = (base, data = {}) => {
  const savedByKey = new Map((data.rules || []).filter(Boolean).map((rule) => [rule.key, rule]));
  const mergedRules = (base.rules || []).map((rule) => ({ ...rule, ...(savedByKey.get(rule.key) || {}) }));
  const baseKeys = new Set((base.rules || []).map((rule) => rule.key));
  for (const savedRule of data.rules || []) {
    if (savedRule?.key && !baseKeys.has(savedRule.key)) mergedRules.push({ ...savedRule });
  }
  return { ...base, ...data, rules: mergedRules };
};


export async function bildirimPolitikalariniGetir() {
  const data = await apiRequest("/notifications/admin/policies");
  const byKey = defaultsByKey();

  (data?.items || []).forEach((item) => {
    const key = item.key || item.id;
    byKey[key] = mergeNotificationPolicy(byKey[key] || { key }, { ...item, key });
  });

  return {
    global: mergeNotificationPolicy(DEFAULT_NOTIFICATION_POLICY_GLOBAL, byKey.global || {}),
    policies: DEFAULT_NOTIFICATION_POLICIES.map((policy) => mergeNotificationPolicy(policy, byKey[policy.key] || {})),
  };
}

export const bildirimPolitikalariGetir = bildirimPolitikalariniGetir;

export async function bildirimPolitikasiKaydet(policyKey, data = {}) {
  await apiRequest(`/notifications/admin/policies/${encodeURIComponent(policyKey)}`, {
    method: "PUT",
    body: JSON.stringify({ key: policyKey, ...data }),
  });
}

export async function bildirimPolitikalariniVarsayilanaKur() {
  await apiRequest("/notifications/admin/policies/defaults", {
    method: "POST",
    body: JSON.stringify({ items: [DEFAULT_NOTIFICATION_POLICY_GLOBAL, ...DEFAULT_NOTIFICATION_POLICIES] }),
  });
}

export const bildirimPolitikalariVarsayilanaKur = bildirimPolitikalariniVarsayilanaKur;

export async function bildirimOtomasyonOzetiniGetir() {
  const data = await apiRequest("/notifications/admin/automation/summary");
  return data?.summary || {
    aktifZamanli: 0,
    bekleyen: 0,
    bugunGonderilen: 0,
    son24SaatGonderilen: 0,
    son24SaatHata: 0,
    sonCalisma: null,
  };
}
